import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { BUSINESS_INFO, OPENING_HOURS } from '../utils/constants';
import { generateWhatsAppMessage, generateEmailLink } from '../utils/helpers';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    postcode: '',
    service: '',
    date: '',
    time: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.postcode.trim()) newErrors.postcode = 'Location/Postcode is required';
    if (!formData.service) newErrors.service = 'Please select a service';
    if (!formData.date) newErrors.date = 'Preferred date is required';
    if (!formData.time) newErrors.time = 'Preferred time is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleWhatsAppSubmit = () => {
    if (validateForm()) {
      const message = generateWhatsAppMessage(formData);
      const whatsappUrl = `${BUSINESS_INFO.whatsappLink}?text=${message}`;
      window.open(whatsappUrl, '_blank');
      setFormData({ name: '', phone: '', email: '', postcode: '', service: '', date: '', time: '', notes: '' });
    }
  };

  const handleEmailSubmit = () => {
    if (validateForm()) {
      const emailLink = generateEmailLink(formData);
      window.location.href = emailLink;
      setFormData({ name: '', phone: '', email: '', postcode: '', service: '', date: '', time: '', notes: '' });
    }
  };

  return (
    <div>
      <section className="section-blue" style={{padding: '4rem 0'}}>
        <div className="container text-center">
          <h1>Get In Touch</h1>
          <p style={{fontSize: '1.25rem', maxWidth: '700px', margin: '0 auto'}}>
            Book your service or reach out with any questions. We're here to help!
          </p>
        </div>
      </section>

      <section className="section section-cream">
        <div className="container">
          <div className="grid grid-2 gap-xl">
            {/* Contact Form */}
            <div>
              <h2 style={{marginBottom: '1.5rem'}}>Book Your Service</h2>
              <Card style={{padding: '5rem'}}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '2.5rem'}}>
                  {/* Name */}
                  <div className="form-group">
                    <label className="form-label">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={`form-input ${errors.name ? 'error' : ''}`}
                      placeholder="Your Full Name"
                    />
                    {errors.name && <div className="form-error">{errors.name}</div>}
                  </div>

                  {/* Phone */}
                  <div className="form-group">
                    <label className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className={`form-input ${errors.phone ? 'error' : ''}`}
                      placeholder="07XXX XXXXXX"
                    />
                    {errors.phone && <div className="form-error">{errors.phone}</div>}
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label className="form-label">
                      Email Address <span style={{color: 'var(--gray-500)'}}>(Optional)</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="form-input"
                      placeholder="john@example.com"
                    />
                  </div>

                  {/* Postcode */}
                  <div className="form-group">
                    <label className="form-label">
                      Postcode / Location <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.postcode}
                      onChange={(e) => handleChange('postcode', e.target.value)}
                      className={`form-input ${errors.postcode ? 'error' : ''}`}
                      placeholder="SW1A 1AA or Home Address"
                    />
                    {errors.postcode && <div className="form-error">{errors.postcode}</div>}
                  </div>

                  {/* Service Selection */}
                  <div className="form-group">
                    <label className="form-label">
                      Service Required <span className="required">*</span>
                    </label>
                    <select
                      value={formData.service}
                      onChange={(e) => handleChange('service', e.target.value)}
                      className={`form-select ${errors.service ? 'error' : ''}`}
                    >
                      <option value="">Select a service...</option>
                      <option value="Bronze Package">Bronze Package</option>
                      <option value="Silver Package">Silver Package</option>
                      <option value="Gold Package">Gold Package</option>
                      <option value="Platinum Package">Platinum Package</option>
                      <option value="Custom Service">Custom Service / Multiple Vehicles</option>
                      <option value="Other">Other (Please specify in notes)</option>
                    </select>
                    {errors.service && <div className="form-error">{errors.service}</div>}
                  </div>

                  {/* Date and Time */}
                  <div className="grid grid-2">
                    <div className="form-group">
                      <label className="form-label">
                        Preferred Date <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => handleChange('date', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className={`form-input ${errors.date ? 'error' : ''}`}
                      />
                      {errors.date && <div className="form-error">{errors.date}</div>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Preferred Time <span className="required">*</span>
                      </label>
                      <input
                        type="time"
                        value={formData.time}
                        onChange={(e) => handleChange('time', e.target.value)}
                        className={`form-input ${errors.time ? 'error' : ''}`}
                      />
                      {errors.time && <div className="form-error">{errors.time}</div>}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="form-group">
                    <label className="form-label">
                      Additional Notes / Requests <span style={{color: 'var(--gray-500)'}}>(Optional)</span>
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      className="form-textarea"
                      placeholder="Any special requirements or additional services needed?"
                    />
                  </div>

                  {/* Submit Buttons */}
                  <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                    <Button 
                      variant="whatsapp" 
                      size="lg" 
                      className="w-full"
                      icon={MessageCircle}
                      onClick={handleWhatsAppSubmit}
                    >
                      Book via WhatsApp
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="w-full"
                      icon={Mail}
                      onClick={handleEmailSubmit}
                    >
                      Send via Email
                    </Button>
                  </div>

                  <p style={{fontSize: '0.875rem', color: 'var(--gray-600)', textAlign: 'center'}}>
                    Choose your preferred contact method. Both will send your booking details.
                  </p>
                </div>
              </Card>
            </div>

            {/* Contact Information */}
            <div>
              <h2 style={{marginBottom: '1.5rem'}}>Contact Information</h2>
              
              <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
                {/* Phone - Clickable WhatsApp */}
                <Card hover className="contact-info-card">
                  <div className="contact-info-icon whatsapp">
                    <Phone size={24} />
                  </div>
                  <div className="contact-info-content">
                    <h3>Phone / WhatsApp</h3>
                    <a 
                      href={BUSINESS_INFO.whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-info-link whatsapp-link"
                    >
                      {BUSINESS_INFO.phoneDisplay}
                    </a>
                    <p className="contact-info-text">
                      Click to chat on WhatsApp instantly
                    </p>
                  </div>
                </Card>

                {/* Email */}
                <Card hover className="contact-info-card">
                  <div className="contact-info-icon email">
                    <Mail size={24} />
                  </div>
                  <div className="contact-info-content">
                    <h3>Email</h3>
                    <a 
                      href={`mailto:${BUSINESS_INFO.email}`}
                      className="contact-info-link"
                    >
                      {BUSINESS_INFO.email}
                    </a>
                    <p className="contact-info-text">
                      Send us an email for quotes or inquiries
                    </p>
                  </div>
                </Card>

                {/* Location */}
                <Card hover className="contact-info-card">
                  <div className="contact-info-icon location">
                    <MapPin size={24} />
                  </div>
                  <div className="contact-info-content">
                    <h3>Service Area</h3>
                    <p style={{color: 'var(--gray-700)'}}>{BUSINESS_INFO.address}</p>
                    <p className="contact-info-text">
                      We provide mobile service across Sheffield UK and surrounding areas
                    </p>
                  </div>
                </Card>

                {/* Opening Hours */}
                <Card hover className="contact-info-card">
                  <div className="contact-info-icon hours">
                    <Clock size={24} />
                  </div>
                  <div className="contact-info-content">
                    <h3>Opening Hours</h3>
                    <div className="contact-hours">
                      <div className="contact-hour-row">
                        <span>{OPENING_HOURS.weekday.days}</span>
                        <span>{OPENING_HOURS.weekday.hours}</span>
                      </div>
                      <div className="contact-hour-row">
                        <span>{OPENING_HOURS.weekend.days}</span>
                        <span>{OPENING_HOURS.weekend.hours}</span>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Quick WhatsApp */}
                <Card style={{padding: '1.5rem', background: 'linear-gradient(134deg, rgba(37, 211, 102, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)', border: '1px solid rgba(37, 211, 102, 0.3)'}}>
                  <h3 style={{marginBottom: '0.5rem', marginLeft: '1.0rem'}}>  Prefer Quick Messaging?</h3>
                  <p style={{marginBottom: '1rem', marginLeft: '1.0rem', color: 'var(--gray-700)'}}>
                    Chat with us directly on WhatsApp for instant responses and easy booking
                  </p>
                  <a href={BUSINESS_INFO.whatsappLink} target="_blank" rel="noopener noreferrer">
                    <Button variant="whatsapp" className="w-full" icon={MessageCircle}>
                      Open WhatsApp Chat
                    </Button>
                  </a>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;